/**
 * DesignTokenProvider - Registry-Driven Design System
 *
 * Fetches design tokens from the database API and applies them as CSS variables.
 * This ensures no hardcoded colors, spacing, or typography in components.
 *
 * Usage:
 *   <DesignTokenProvider>
 *     <App />
 *   </DesignTokenProvider>
 *
 * In components, use CSS variables:
 *   color: var(--color-primary-500);
 *   padding: var(--spacing-md);
 *   font-family: var(--font-body);
 */

import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

const API_BASE = import.meta.env.VITE_API_URL || 'https://sales-api.ait.best';

// Types
interface DesignToken {
  value: string;
  css_var: string;
  description?: string;
}

interface TokenCategory {
  [tokenName: string]: DesignToken;
}

interface TokensConfig {
  color?: TokenCategory;
  spacing?: TokenCategory;
  typography?: TokenCategory;
  shadow?: TokenCategory;
  radius?: TokenCategory;
  animation?: TokenCategory;
}

interface IconDefinition {
  name: string;
  lucide: string | null;
  display_name: string;
  sublabel?: string;
  color_token?: string;
}

interface IconsConfig {
  [category: string]: IconDefinition[];
}

interface SettingsConfig {
  [category: string]: {
    [key: string]: string | number | boolean | object;
  };
}

interface PageState {
  state_name: string;
  display_name: string;
  component_name: string;
  allowed_block_types: string[];
  features: Record<string, boolean>;
  can_navigate_to: string[];
  route_pattern?: string;
}

interface BlockType {
  type_name: string;
  display_name: string;
  category: string;
  icon_name?: string;
  default_display_hints: Record<string, unknown>;
  supports_content_items: boolean;
  supports_image: boolean;
  supports_products: boolean;
}

interface LearningConfig {
  tokens: TokensConfig;
  icons: IconsConfig;
  settings: SettingsConfig;
  page_states: Record<string, PageState>;
  block_types: Record<string, BlockType>;
  components: Record<string, unknown>;
  version: string;
}

interface DesignTokenContextValue {
  tokens: TokensConfig;
  icons: IconsConfig;
  settings: SettingsConfig;
  pageStates: Record<string, PageState>;
  blockTypes: Record<string, BlockType>;
  isLoading: boolean;
  error: Error | null;
  getToken: (category: string, name: string) => string | undefined;
  getIcon: (name: string) => IconDefinition | undefined;
  getSetting: (category: string, key: string) => unknown;
  getPageState: (name: string) => PageState | undefined;
  getBlockType: (name: string) => BlockType | undefined;
}

// Context
const DesignTokenContext = createContext<DesignTokenContextValue | null>(null);

// API fetch function with authentication
async function fetchLearningConfig(): Promise<LearningConfig> {
  const { data: { session } } = await supabase.auth.getSession();

  if (!session?.access_token) {
    throw new Error('No active session');
  }

  const response = await fetch(`${API_BASE}/api/learning/v2/config`, {
    headers: {
      'Authorization': `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch learning config: ${response.status}`);
  }

  return response.json();
}

// Provider component
interface DesignTokenProviderProps {
  children: ReactNode;
}

export function DesignTokenProvider({ children }: DesignTokenProviderProps) {
  const [cssApplied, setCssApplied] = useState(false);

  const {
    data: config,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['learning-config'],
    queryFn: fetchLearningConfig,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });

  // Apply CSS variables when tokens change
  useEffect(() => {
    if (!config?.tokens) return;

    const root = document.documentElement;

    // Apply all token categories
    Object.entries(config.tokens).forEach(([_category, tokens]) => {
      Object.values(tokens).forEach((token) => {
        if (token.css_var && token.value) {
          root.style.setProperty(token.css_var, token.value);
        }
      });
    });

    setCssApplied(true);
  }, [config?.tokens]);

  // Helper functions
  const getToken = (category: string, name: string): string | undefined => {
    return config?.tokens?.[category as keyof TokensConfig]?.[name]?.value;
  };

  const getIcon = (name: string): IconDefinition | undefined => {
    if (!config?.icons) return undefined;

    for (const category of Object.values(config.icons)) {
      const icon = category.find((i) => i.name === name);
      if (icon) return icon;
    }
    return undefined;
  };

  const getSetting = (category: string, key: string): unknown => {
    return config?.settings?.[category]?.[key];
  };

  const getPageState = (name: string): PageState | undefined => {
    return config?.page_states?.[name];
  };

  const getBlockType = (name: string): BlockType | undefined => {
    return config?.block_types?.[name];
  };

  const contextValue: DesignTokenContextValue = {
    tokens: config?.tokens || {},
    icons: config?.icons || {},
    settings: config?.settings || {},
    pageStates: config?.page_states || {},
    blockTypes: config?.block_types || {},
    isLoading,
    error: error as Error | null,
    getToken,
    getIcon,
    getSetting,
    getPageState,
    getBlockType,
  };

  // Show children even while loading - CSS will be applied once ready
  return (
    <DesignTokenContext.Provider value={contextValue}>
      {children}
      {!cssApplied && isLoading && (
        <style>
          {`
            /* Fallback tokens while loading */
            :root {
              --color-primary-500: #3B82F6;
              --color-success-500: #22C55E;
              --color-warning-500: #F59E0B;
              --color-error-500: #EF4444;
              --spacing-xs: 4px;
              --spacing-sm: 8px;
              --spacing-md: 16px;
              --spacing-lg: 24px;
              --spacing-xl: 32px;
              --font-body: Inter, system-ui, sans-serif;
              --font-heading: Inter, system-ui, sans-serif;
              --radius-sm: 4px;
              --radius-md: 8px;
              --radius-lg: 12px;
            }
          `}
        </style>
      )}
    </DesignTokenContext.Provider>
  );
}

// Custom hook for consuming design tokens
export function useDesignTokens(): DesignTokenContextValue {
  const context = useContext(DesignTokenContext);

  if (!context) {
    throw new Error('useDesignTokens must be used within a DesignTokenProvider');
  }

  return context;
}

// Convenience hooks for specific token types
export function useColorToken(name: string): string {
  const { getToken } = useDesignTokens();
  return getToken('color', name) || '#000000';
}

export function useSpacingToken(name: string): string {
  const { getToken } = useDesignTokens();
  return getToken('spacing', name) || '16px';
}

export function useTypographyToken(name: string): string {
  const { getToken } = useDesignTokens();
  return getToken('typography', name) || 'Inter';
}

// Hook for page state features
export function usePageFeatures(stateName: string): Record<string, boolean> {
  const { getPageState } = useDesignTokens();
  const state = getPageState(stateName);
  return state?.features || {};
}

// Hook for allowed block types on a page
export function useAllowedBlockTypes(stateName: string): string[] {
  const { getPageState } = useDesignTokens();
  const state = getPageState(stateName);
  return state?.allowed_block_types || [];
}

export default DesignTokenProvider;
